/**
 * Festival and Set Processing Utilities
 * Pure business logic for processing festival data
 */

import type {
	DateTimePreferences,
	FestivalDetails,
	FetchedDbSet,
	ProcessedSet,
} from "@workspace/types";
import {
	formatTimeWithPreferences,
} from "../formatting/datetime-preferences";

/**
 * Day names array for consistent ordering
 */
export const DAYS_OF_WEEK = [
	"sunday",
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday",
] as const;


/**
 * Calculate the effective day for a set, accounting for overnight festivals
 * @param startTime - The set's start time
 * @param festivalDetails - Festival configuration including overnight settings
 * @returns The day of week as lowercase string
 */
export function calculateSetDay(
	startTime: Date,
	festivalDetails?: FestivalDetails | null,
): string {
	// Set timestamps are stored as "timestamp without timezone" representing
	// festival local time. Local methods are correct here.
	let dayIndex = startTime.getDay();

	// Handle overnight festivals
	if (
		festivalDetails?.schedule_overnight &&
		festivalDetails?.schedule_day_start_hour !== undefined
	) {
		const hour = startTime.getHours();
		// If the set starts before the day boundary, it belongs to the previous day
		if (hour < festivalDetails.schedule_day_start_hour) {
			const prevDay = new Date(startTime);
			prevDay.setDate(prevDay.getDate() - 1);
			dayIndex = prevDay.getDay();
		}
	}

	return DAYS_OF_WEEK[dayIndex] || "unknown";
}

/**
 * Calculate the sort hour for a set, adjusting for overnight festivals
 * @param startTime - The set's start time
 * @param festivalDetails - Festival configuration
 * @returns Hour value adjusted for sorting (can be > 24 for overnight sets)
 */
export function calculateSortHour(
	startTime: Date,
	festivalDetails?: FestivalDetails | null,
): number {
	let startHour = startTime.getHours() + startTime.getMinutes() / 60;

	// For overnight festivals, adjust hours before the day start to sort at the end
	// Only add +24 if the schedule actually crosses midnight (starts in evening, ends in morning)
	// Don't add +24 for after parties that start AT midnight (schedule_start_hour = 0)
	const scheduleStartHour = festivalDetails?.schedule_start_hour ?? 0;
	const scheduleEndHour = festivalDetails?.schedule_end_hour ?? 24;
	const scheduleActuallyCrossesMidnight = scheduleStartHour > scheduleEndHour;

	if (
		festivalDetails?.schedule_overnight &&
		festivalDetails?.schedule_day_start_hour !== undefined &&
		startHour < festivalDetails.schedule_day_start_hour &&
		scheduleActuallyCrossesMidnight
	) {
		// Add 24 to make these sets sort after the regular day's sets
		startHour += 24;
	}

	return startHour;
}

/**
 * Process raw database sets into ProcessedSet objects with calculated properties
 * @param dbSets - Raw sets from database
 * @param festivalDetailsData - Festival configuration
 * @param preferences - User's datetime preferences
 * @returns Array of processed sets with calculated properties
 */
export function processDbSets(
	dbSets: FetchedDbSet[],
	festivalDetailsData?: FestivalDetails | null,
	preferences?: Partial<DateTimePreferences>,
): ProcessedSet[] {
	return dbSets.map((set): ProcessedSet => {
		// Handle lineup-only festivals (no schedule/time data)
		if (
			!set.start_time ||
			!set.end_time ||
			set.start_time === "" ||
			set.end_time === ""
		) {
			return createLineupOnlySet(set);
		}

		// Normal processing for festivals with complete schedules
		const startTime = new Date(set.start_time);
		const endTime = new Date(set.end_time);

		const dayOfWeek = calculateSetDay(startTime, festivalDetailsData);
		const startHour = calculateSortHour(startTime, festivalDetailsData);

		const formatTime = (date: Date) =>
			formatTimeWithPreferences(date, preferences);

		const durationMs = endTime.getTime() - startTime.getTime();
		const durationHours = durationMs / (1000 * 60 * 60);

		const processedSet: ProcessedSet = {
			id: set.id,
			artist: set.artist
				? {
						...set.artist,
						aliases:
							(set.artist as { aliases?: { alias_name: string }[] }).aliases ||
							[],
					}
				: null,
			stage: set.stage,
			start_time: set.start_time,
			end_time: set.end_time,
			youtube_url: set.youtube_url,
			alias_name: set.alias_name,
			is_group_alias: set.is_group_alias,
			group_alias_slug: set.group_alias_slug,
			group_alias_name: set.group_alias_name,
			member_artists: set.member_artists,
			presents_name: set.presents_name,
			friends_set: set.friends_set,
			is_collaborative: set.is_collaborative,
			display_format: set.display_format,
			is_surprise: set.is_surprise,
			placeholder_name: set.placeholder_name,
			special_guests: set.special_guests,
			set_artists: Array.isArray(set.set_artists) ? set.set_artists : [],
			day: dayOfWeek,
			startHour,
			durationHours,
			time: `${formatTime(startTime)}-${formatTime(endTime)}`,
			isLineupOnly: festivalDetailsData?.schedule_status === "lineup_only",
			// Preserve event_type field for filtering (pre/after party support)
			...("event_type" in set ? { event_type: set.event_type } : {}),
			// Preserve program set data if it exists
			...("_program_set" in set
				? {
						_program_set: (
							set as {
								_program_set?: {
									name: string;
									program_type: string;
									registration_required: boolean;
									description: string | null;
								};
							}
						)._program_set,
					}
				: {}),
		};
		return processedSet;
	});
}

/**
 * Create a lineup-only set (no schedule information)
 * @param set - Raw database set
 * @returns Processed set for lineup-only display
 */
function createLineupOnlySet(set: FetchedDbSet): ProcessedSet {
	const processedSet: ProcessedSet = {
		id: set.id,
		artist: set.artist
			? {
					...set.artist,
					aliases:
						(set.artist as { aliases?: { alias_name: string }[] }).aliases ||
						[],
				}
			: null,
		stage: set.stage,
		start_time: set.start_time,
		end_time: set.end_time,
		youtube_url: set.youtube_url,
		alias_name: set.alias_name,
		is_group_alias: set.is_group_alias,
		group_alias_slug: set.group_alias_slug,
		group_alias_name: set.group_alias_name,
		member_artists: set.member_artists,
		presents_name: set.presents_name,
		friends_set: set.friends_set,
		is_collaborative: set.is_collaborative,
		display_format: set.display_format,
		is_surprise: set.is_surprise,
		placeholder_name: set.placeholder_name,
		special_guests: set.special_guests,
		set_artists: Array.isArray(set.set_artists) ? set.set_artists : [],
		day: "all", // No specific day for lineup-only
		startHour: 0,
		durationHours: 0,
		time: "TBA", // Placeholder time
		isLineupOnly: true,
		// Preserve event_type field for filtering (pre/after party support)
		...("event_type" in set ? { event_type: set.event_type } : {}),
		// Preserve program set data if it exists
		...("_program_set" in set
			? {
					_program_set: (
						set as {
							_program_set?: {
								name: string;
								program_type: string;
								registration_required: boolean;
								description: string | null;
							};
						}
					)._program_set,
				}
			: {}),
	};
	return processedSet;
}

/**
 * Get time label for hour with user preferences
 * @param hour - Hour value (0-23, or > 24 for overnight)
 * @param preferences - User's datetime preferences
 * @returns Formatted time label
 */
export function getTimeLabel(
	hour: number,
	preferences?: Partial<DateTimePreferences>,
): string {
	const { time_format = "12h" } = preferences || {};
	
	// Handle overnight hours
	const normalizedHour = hour % 24;
	
	// Return full format with minutes (e.g., "12:00PM") to match web app
	// This ensures consistent grouping between web and mobile
	if (time_format === "24h") {
		return `${normalizedHour.toString().padStart(2, "0")}:00`;
	}
	
	// 12-hour format with full time (matches web app)
	if (normalizedHour === 0) return "12:00AM";
	if (normalizedHour === 12) return "12:00PM";
	if (normalizedHour < 12) return `${normalizedHour}:00AM`;
	return `${normalizedHour - 12}:00PM`;
}

/**
 * Get unique days from sets and sort them properly
 * @param sets - Array of processed sets
 * @param festival - Festival details for date-based ordering
 * @returns Sorted array of unique day names
 */
export function getUniqueDays(
	sets: ProcessedSet[],
	festival?: FestivalDetails | null,
): string[] {
	const days = new Set<string>();
	for (const set of sets) {
		// Use the pre-calculated day property from the processed set
		if (set.day && set.day !== "all") {
			days.add(set.day);
		}
	}

	const uniqueDays = Array.from(days);

	// If we have festival dates, use them to determine the correct day order
	// Uses UTC to avoid local timezone shifting the start day
	if (festival?.start_date && festival.end_date) {
		const dayNamesToIndex = new Map<string, number>();
		const startDate = new Date(festival.start_date);
		const endDate = new Date(festival.end_date);

		// Map each day name to its position in the festival schedule
		for (let i = 0; i < 14; i++) {
			const date = new Date(startDate);
			date.setUTCDate(startDate.getUTCDate() + i);
			const dayName = DAYS_OF_WEEK[date.getUTCDay()];

			if (dayName && !dayNamesToIndex.has(dayName)) dayNamesToIndex.set(dayName, i);
			if (date >= endDate) break;
		}

		// Sort by festival day order
		return uniqueDays.sort((a, b) => {
			const indexA = dayNamesToIndex.get(a) ?? 999;
			const indexB = dayNamesToIndex.get(b) ?? 999;
			return indexA - indexB;
		});
	}

	// Fallback to alphabetical order if no festival dates (should never happen in production)
	return uniqueDays.sort((a, b) => a.localeCompare(b));
}

/**
 * Get the default day to show when opening a festival.
 * If the festival is currently happening, defaults to today's festival day.
 * Otherwise defaults to the first day.
 */
export function getDefaultDay(
	availableDays: string[],
	festival?: FestivalDetails | null,
): string {
	if (availableDays.length === 0) return "";
	const firstDay = availableDays[0] ?? "";
	if (!festival?.start_date || !festival?.end_date) return firstDay;

	const now = new Date();
	const startDate = new Date(festival.start_date);
	const endDate = new Date(festival.end_date);

	// Festival dates are stored as YYYY-MM-DD (date-only), parsed as UTC midnight.
	// Use UTC methods to avoid local timezone shifting the date by ±1 day.
	const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const festStart = new Date(
		startDate.getUTCFullYear(),
		startDate.getUTCMonth(),
		startDate.getUTCDate(),
	);
	const festEnd = new Date(
		endDate.getUTCFullYear(),
		endDate.getUTCMonth(),
		endDate.getUTCDate(),
	);

	// For overnight festivals, extend the end range by 1 day
	// (last day's sets can run past midnight into the next calendar day)
	if (festival.schedule_overnight) {
		festEnd.setDate(festEnd.getDate() + 1);
	}

	// Check if today falls within the festival date range
	if (todayDate < festStart || todayDate > festEnd) {
		return firstDay; // Past or future festival
	}

	// Determine the "festival day" name for right now
	let dayIndex = now.getDay();

	// Account for overnight: if it's 2am, the festival day is still "yesterday"
	if (
		festival.schedule_overnight &&
		festival.schedule_day_start_hour !== undefined
	) {
		const hour = now.getHours();
		if (hour < festival.schedule_day_start_hour) {
			const prev = new Date(now);
			prev.setDate(prev.getDate() - 1);
			dayIndex = prev.getDay();
		}
	}

	const todayName = DAYS_OF_WEEK[dayIndex] || "";

	if (todayName && availableDays.includes(todayName)) {
		return todayName;
	}

	return firstDay;
}

/**
 * Filter sets by day
 * @param sets - Array of processed sets
 * @param day - Day to filter by (or "all" for no filter)
 * @returns Filtered array of sets
 */
export function filterSetsByDay(
	sets: ProcessedSet[],
	day: string | null | undefined,
): ProcessedSet[] {
	if (!day || day === "all" || day === "") return sets;
	return sets.filter((set) => set.day === day);
}

/**
 * Sort sets by time and stage
 * @param sets - Array of sets to sort
 * @param formatStageName - Optional function to format stage names
 * @returns Sorted array (mutates original)
 */
export function sortSetsByTimeAndStage(
	sets: ProcessedSet[],
	formatStageName?: (name: string | undefined) => string,
): ProcessedSet[] {
	return sets.sort((a, b) => {
		const timeA = a.start_time ? new Date(a.start_time).getTime() : 0;
		const timeB = b.start_time ? new Date(b.start_time).getTime() : 0;

		if (timeA !== timeB) return timeA - timeB;

		// If times are equal, sort by stage name
		const stageA = formatStageName
			? formatStageName(a.stage?.name)
			: a.stage?.name || "";
		const stageB = formatStageName
			? formatStageName(b.stage?.name)
			: b.stage?.name || "";

		return stageA.localeCompare(stageB);
	});
}

/**
 * Get the current hour and minute at a specific IANA timezone using Intl.
 * Falls back to device local time if timezone is not provided.
 */
function getNowInTimezone(timezone?: string | null): { hour: number; minute: number; dayOfWeek: number } {
	const now = new Date();

	if (timezone) {
		try {
			const formatter = new Intl.DateTimeFormat("en-US", {
				timeZone: timezone,
				hour: "numeric",
				minute: "numeric",
				weekday: "short",
				hour12: false,
			});
			const parts = formatter.formatToParts(now);
			const hour = Number.parseInt(parts.find((p) => p.type === "hour")?.value ?? "0", 10);
			const minute = Number.parseInt(parts.find((p) => p.type === "minute")?.value ?? "0", 10);
			const weekdayStr = parts.find((p) => p.type === "weekday")?.value?.toLowerCase() ?? "";
			const dayMap: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
			const dayOfWeek = dayMap[weekdayStr] ?? now.getDay();
			return { hour: hour === 24 ? 0 : hour, minute, dayOfWeek };
		} catch {
			// Invalid timezone string — fall through to local time
		}
	}

	return { hour: now.getHours(), minute: now.getMinutes(), dayOfWeek: now.getDay() };
}

/**
 * Check if a selected day name (e.g., "friday") corresponds to today's date
 * for a given festival. Uses the festival's timezone to determine what "today"
 * means at the festival location (e.g., for livestream viewers in other timezones).
 * Accounts for overnight festivals where the "day" extends past midnight.
 */
export function isDayToday(
	selectedDay: string,
	festival?: FestivalDetails | null,
): boolean {
	if (!selectedDay || !festival?.start_date || !festival?.end_date) return false;

	const startDate = new Date(festival.start_date);
	const endDate = new Date(festival.end_date);

	// Build festival date range using UTC to avoid local timezone shifting
	const festStart = new Date(
		startDate.getUTCFullYear(),
		startDate.getUTCMonth(),
		startDate.getUTCDate(),
	);
	const festEnd = new Date(
		endDate.getUTCFullYear(),
		endDate.getUTCMonth(),
		endDate.getUTCDate(),
	);

	// Get current time at the festival's timezone
	const { hour, dayOfWeek } = getNowInTimezone(festival.timezone);
	let currentDayOfWeek = dayOfWeek;

	// For overnight festivals, if it's past midnight but before the "day start",
	// the festival "day" is still yesterday
	if (festival.schedule_overnight && festival.schedule_day_start_hour !== undefined) {
		if (hour < festival.schedule_day_start_hour) {
			// Shift day back by 1
			currentDayOfWeek = (currentDayOfWeek - 1 + 7) % 7;
		}
	}

	// Get today's date at the festival timezone for range check
	let todayDate: Date;
	if (festival.timezone) {
		try {
			const formatter = new Intl.DateTimeFormat("en-CA", {
				timeZone: festival.timezone,
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
			});
			// en-CA gives YYYY-MM-DD format
			const dateStr = formatter.format(new Date());
			const [y, m, d] = dateStr.split("-").map(Number);
			todayDate = new Date(y!, m! - 1, d!);
			// Adjust for overnight
			if (festival.schedule_overnight && festival.schedule_day_start_hour !== undefined && hour < festival.schedule_day_start_hour) {
				todayDate.setDate(todayDate.getDate() - 1);
			}
		} catch {
			todayDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
		}
	} else {
		const now = new Date();
		todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		if (festival.schedule_overnight && festival.schedule_day_start_hour !== undefined && hour < festival.schedule_day_start_hour) {
			todayDate.setDate(todayDate.getDate() - 1);
		}
	}

	// Check if today (at festival timezone) is within the festival range
	if (todayDate < festStart || todayDate > festEnd) return false;

	const todayName = DAYS_OF_WEEK[currentDayOfWeek] || "";
	return todayName === selectedDay;
}

/**
 * Get the current time's position as decimal hours from the schedule grid start.
 * Uses the festival's timezone so the indicator shows what's playing at the
 * festival right now, regardless of the user's local timezone.
 * Returns null if the current time is outside the schedule range.
 *
 * Example: If scheduleStartHour=17 and it's 19:30 at the festival, returns 2.5
 */
export function getCurrentTimePosition(
	scheduleStartHour: number,
	scheduleEndHour: number,
	scheduleIsOvernight: boolean,
	scheduleDayStartHour?: number,
	timezone?: string | null,
): number | null {
	const { hour, minute } = getNowInTimezone(timezone);
	let currentHour = hour + minute / 60;

	// For overnight festivals, if current hour is in the "after midnight" zone,
	// add 24 to make it comparable with the schedule range
	if (scheduleIsOvernight && scheduleDayStartHour !== undefined) {
		if (currentHour < scheduleDayStartHour) {
			currentHour += 24;
		}
	} else if (scheduleIsOvernight && scheduleEndHour < scheduleStartHour) {
		if (currentHour < scheduleStartHour && currentHour < scheduleEndHour) {
			currentHour += 24;
		}
	}

	// Calculate effective end hour for range check
	let effectiveEndHour = scheduleEndHour;
	if (scheduleIsOvernight && scheduleEndHour < scheduleStartHour) {
		effectiveEndHour = scheduleEndHour + 24;
	} else if (scheduleEndHour === 24 && !scheduleIsOvernight) {
		effectiveEndHour = 24;
	}

	// Check if current time is within the schedule range
	if (currentHour < scheduleStartHour || currentHour > effectiveEndHour) {
		return null;
	}

	return currentHour - scheduleStartHour;
}